import { useEffect, useRef, useState, useSyncExternalStore } from "react"

import { Bug, Crosshair } from "@/components/icons"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

import { useDashDevtools } from "./devtools-context"

import { type DevtoolsEvent } from "./event-store"
import { type PickerSession, setHoverHighlight, startPicker } from "./picker"

import { aggregateUsage, formatTokens } from "./usage-aggregate"
import { useDashboardStateStore } from "../state/dashboard-state"

/**
 * Devtools panel for inspecting a live Dash dashboard.
 *
 * Renders a floating "Dash devtools" trigger button at the bottom-right.
 * Opens into a right-side Sheet with five tabs:
 * - **Actions**: action-dispatched + action-settled events. Most useful
 *   for "did my Button fire?" / "why did this dispatch fail?".
 * - **Streams**: stream-event + stream-usage events. Most useful for
 *   "how many tokens did this generation cost?" and "is the cache
 *   firing?".
 * - **Spec**: live spec snapshot.
 * - **State**: live dashboard state.
 * - **All**: unfiltered firehose of every event for cases where the
 *   above slicing hides the one you want.
 *
 * Mount only when {@link shouldEnableDashDevtools} returns true — see
 * the gating in `dashboard-renderer.tsx`.
 */
export function DashDevtoolsPanel() {
  const ctx = useDashDevtools()
  const stateStore = useDashboardStateStore()
  const [open, setOpen] = useState(false)
  const [pickedKey, setPickedKey] = useState<string | null>(null)
  const pickerRef = useRef<PickerSession | null>(null)

  function startPick() {
    pickerRef.current?.stop()
    pickerRef.current = startPicker({
      onPick: (key) => {
        setPickedKey(key)
        setHoverHighlight(key)
        pickerRef.current = null
      },
      onCancel: () => {
        pickerRef.current = null
      },
    })
  }
  // Tear down any active picker + highlight when the panel unmounts.
  useEffect(() => {
    return () => {
      pickerRef.current?.stop()
      setHoverHighlight(null)
    }
  }, [])

  // Subscribe to event-store updates with React's useSyncExternalStore.
  const events = useSyncExternalStore(
    ctx ? ctx.eventStore.subscribe : noopSubscribe,
    () => (ctx ? ctx.eventStore.list() : EMPTY_EVENTS),
    () => EMPTY_EVENTS
  )

  // Subscribe to dashboard state for the State tab.
  const state = useSyncExternalStore(
    stateStore.subscribe,
    () => stateStore.getSnapshot(),
    () => stateStore.getSnapshot()
  )

  // Cmd/Ctrl + Shift + D toggles the panel. Mirrors json-render's
  // Cmd+Shift+J convention but `J` is taken by browser's JS console
  // toggle in some setups.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "D" && e.shiftKey && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((v) => !v)
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [])

  if (!ctx) return null

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          aria-label="Open Dash devtools"
          className="fixed right-4 bottom-4 z-50 shadow-lg"
          size="icon"
          variant="outline"
        >
          <Bug className="size-4" />
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-xl" side="right">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            Dash devtools
            <Badge variant="secondary">{events.length} events</Badge>
            <Button
              aria-label="Pick element"
              className="ml-auto"
              size="sm"
              variant="outline"
              onClick={startPick}
            >
              <Crosshair className="mr-1 size-3" />
              Pick
            </Button>
          </SheetTitle>
          {pickedKey && (
            <div className="text-muted-foreground flex items-center gap-2 pt-1 font-mono text-xs">
              Picked:
              <code
                className="bg-muted rounded px-1 py-0.5"
                onMouseEnter={() => setHoverHighlight(pickedKey)}
                onMouseLeave={() => setHoverHighlight(null)}
              >
                {pickedKey}
              </code>
              <Button
                aria-label="Clear picked element"
                className="-my-0.5 h-6 px-1.5 text-xs"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setPickedKey(null)
                  setHoverHighlight(null)
                }}
              >
                clear
              </Button>
            </div>
          )}
          <UsageStrip events={events} />
        </SheetHeader>

        <Tabs className="mt-4 flex h-[calc(100%-4rem)] flex-col" defaultValue="actions">
          <TabsList>
            <TabsTrigger value="actions">Actions</TabsTrigger>
            <TabsTrigger value="streams">Streams</TabsTrigger>
            <TabsTrigger value="spec">Spec</TabsTrigger>
            <TabsTrigger value="state">State</TabsTrigger>
            <TabsTrigger value="all">All</TabsTrigger>
          </TabsList>

          <TabsContent className="mt-2 flex-1 overflow-hidden" value="actions">
            <ScrollArea className="h-full">
              <FilteredEventList
                emptyText="No actions yet. Click a Button or fire a watch binding to populate."
                events={events}
                kinds={ACTION_EVENT_KINDS}
              />
            </ScrollArea>
          </TabsContent>

          <TabsContent className="mt-2 flex-1 overflow-hidden" value="streams">
            <ScrollArea className="h-full">
              <FilteredEventList
                emptyText="No streams yet. Ask the agent to generate or refine a dashboard."
                events={events}
                kinds={STREAM_EVENT_KINDS}
              />
            </ScrollArea>
          </TabsContent>

          <TabsContent className="mt-2 flex-1 overflow-hidden" value="spec">
            <ScrollArea className="h-full">
              <SpecPreview getSpec={ctx.getCurrentSpec} />
            </ScrollArea>
          </TabsContent>

          <TabsContent className="mt-2 flex-1 overflow-hidden" value="state">
            <ScrollArea className="h-full">
              <pre className="text-muted-foreground bg-muted/30 rounded p-2 font-mono text-xs">
                {safeStringify(state)}
              </pre>
            </ScrollArea>
          </TabsContent>

          <TabsContent className="mt-2 flex-1 overflow-hidden" value="all">
            <ScrollArea className="h-full">
              {events.length === 0 ? (
                <EmptyHint text="No events yet. Interact with the dashboard or stream a spec to populate." />
              ) : (
                <ul className="space-y-1.5 pr-3 font-mono text-xs">
                  {events.map((event, i) => (
                    <EventRow key={`${event.at}-${i}`} event={event} />
                  ))}
                </ul>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  )
}

const EMPTY_EVENTS: DevtoolsEvent[] = []
function noopSubscribe(): () => void {
  return () => {}
}

const ACTION_EVENT_KINDS = ["action-dispatched", "action-settled"] as const
const STREAM_EVENT_KINDS = ["stream-event", "stream-usage"] as const

type FilterKind = (typeof ACTION_EVENT_KINDS)[number] | (typeof STREAM_EVENT_KINDS)[number]

function FilteredEventList({
  events,
  kinds,
  emptyText,
}: {
  events: DevtoolsEvent[]
  kinds: readonly FilterKind[]
  emptyText: string
}) {
  const kindSet = new Set<string>(kinds)
  const filtered = events.filter((e) => kindSet.has(e.kind))
  if (filtered.length === 0) return <EmptyHint text={emptyText} />
  return (
    <ul className="space-y-1.5 pr-3 font-mono text-xs">
      {filtered.map((event, i) => (
        <EventRow key={`${event.at}-${i}`} event={event} />
      ))}
    </ul>
  )
}

function EmptyHint({ text }: { text: string }) {
  return <p className="text-muted-foreground p-4 text-sm">{text}</p>
}

function EventRow({ event }: { event: DevtoolsEvent }) {
  const ts = new Date(event.at).toISOString().slice(11, 23)
  switch (event.kind) {
    case "action-dispatched":
      return (
        <li>
          <span className="text-muted-foreground">{ts}</span>{" "}
          <Badge className="mr-1" variant="outline">
            dispatch
          </Badge>
          <span className="text-foreground">{event.info.name}</span>
          {event.info.params && (
            <span className="text-muted-foreground"> {safeStringify(event.info.params)}</span>
          )}
        </li>
      )
    case "action-settled":
      return (
        <li>
          <span className="text-muted-foreground">{ts}</span>{" "}
          <Badge className="mr-1" variant={event.info.ok ? "default" : "destructive"}>
            {event.info.ok ? "ok" : "fail"}
          </Badge>
          <span className="text-foreground">{event.info.name}</span>
          <span className="text-muted-foreground"> {event.info.durationMs}ms</span>
          {!event.info.ok && event.info.error !== undefined && (
            <span className="text-destructive"> {String(event.info.error)}</span>
          )}
        </li>
      )
    case "spec-changed":
      return (
        <li>
          <span className="text-muted-foreground">{ts}</span>{" "}
          <Badge className="mr-1" variant="secondary">
            spec
          </Badge>
          <span className="text-muted-foreground">{event.specJson.length} chars</span>
        </li>
      )
    case "state-changed":
      return (
        <li>
          <span className="text-muted-foreground">{ts}</span>{" "}
          <Badge className="mr-1" variant="secondary">
            state
          </Badge>
          <span className="text-muted-foreground">{Object.keys(event.snapshot).length} keys</span>
        </li>
      )
    case "stream-event":
      return (
        <li>
          <span className="text-muted-foreground">{ts}</span>{" "}
          <Badge className="mr-1" variant="outline">
            stream:{event.phase}
          </Badge>
          {event.detail && <span className="text-muted-foreground">{event.detail}</span>}
        </li>
      )
    case "stream-usage":
      return (
        <li>
          <span className="text-muted-foreground">{ts}</span>{" "}
          <Badge className="mr-1" variant="secondary">
            usage
          </Badge>
          <span className="text-foreground">{formatTokens(event.usage.totalTokens)}</span>
          <span className="text-muted-foreground">
            {" "}
            ({formatTokens(event.usage.promptTokens)}/{formatTokens(event.usage.completionTokens)}
            {event.usage.cachedTokens && event.usage.cachedTokens > 0
              ? ` · cache ${formatTokens(event.usage.cachedTokens)}`
              : ""}
            )
          </span>
        </li>
      )
    default:
      return null
  }
}

function SpecPreview({ getSpec }: { getSpec: () => unknown }) {
  // Re-read on every panel render — cheap enough for a debug surface.
  const spec = getSpec()
  return (
    <pre className="text-muted-foreground bg-muted/30 rounded p-2 font-mono text-xs">
      {spec ? safeStringify(spec) : "(no spec)"}
    </pre>
  )
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return "<unserialisable>"
  }
}

/**
 * Header strip showing accumulated token usage since the panel opened.
 * Hidden when no `stream-usage` events have landed (i.e. no AI
 * generation has happened in this session yet).
 *
 * Cache hit rate is shown as `cached/prompt%` so the developer can
 * tell at a glance whether the prompt-cache is firing — high hit rate
 * means the agent is reusing cached system-prompt prefixes.
 */
function UsageStrip({ events }: { events: DevtoolsEvent[] }) {
  const usage = aggregateUsage(events)
  if (usage.generations === 0) return null
  return (
    <div className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 pt-1 font-mono text-xs">
      <span>
        <strong className="text-foreground">{formatTokens(usage.totalTokens)}</strong> tokens
      </span>
      <span>
        prompt {formatTokens(usage.promptTokens)} · completion{" "}
        {formatTokens(usage.completionTokens)}
      </span>
      {usage.cachedTokens > 0 && (
        <span>
          cache {formatTokens(usage.cachedTokens)}
          {usage.cacheHitRate !== null && ` · ${Math.round(usage.cacheHitRate * 100)}% hit`}
        </span>
      )}
      <span>
        {usage.generations} {usage.generations === 1 ? "gen" : "gens"}
      </span>
      {usage.lastModel && <span>· {usage.lastModel}</span>}
    </div>
  )
}
